import * as PIXI from 'pixi.js';

import type { SeekMode } from '../../../enums';
import type { Transformation as TransformationType } from '../../../types';
import { Config } from '../../../video-data/config';
import { getPositionFromTransformation } from '../../../video-data/designs/scenes';
import type { LoadSettings } from '../../serializableMixin';
import { DynamicStylingMixin } from '../../dynamicStylingMixin';
import type { BaseAssetSettings } from '../BaseAsset';
import { BaseAsset } from '../BaseAsset';
import { resetSprite } from '../utils';
import { StringAttribute } from '../../attributes';
import { getGlobalFontManager } from '../../FontManager';
import { applyLabelOverflowDefaults } from './applyLabelOverflowDefaults';
import { ChartRenderer } from './ChartRenderer';
import { deserializeFunctions, serializeFunctions } from './function-serialization';
import type { ChartConfig, ChartPalette, ChartSeriesType } from './types';

type ChartAssetSettings = BaseAssetSettings;

export class ChartAsset extends DynamicStylingMixin(BaseAsset) {
    override _classType = Config.CHART_ASSET_TYPE;

    #width = 800;
    #height = 600;
    #config: ChartConfig | null = null;
    #resolvedConfig: ChartConfig | null = null;
    #progress = 1;

    #sprite: PIXI.Sprite;
    #renderer: ChartRenderer;

    #resolvedSource: { rawConfig: ChartConfig; paletteKey: string } | null = null;

    constructor(container: unknown, settings: ChartAssetSettings) {
        super(container, settings);

        this.#renderer = new ChartRenderer();
        this.#sprite = new PIXI.Sprite();
        this._addPixiChild(this.#sprite);
    }

    override destroy() {
        this.#renderer.destroy();
        super.destroy(false);
        this.#sprite.destroy(false);
    }

    override get failedToLoad() {
        return false;
    }

    override get isLoaded() {
        return true;
    }

    override get _shouldLoad() {
        return true;
    }

    override get width() {
        return this.#width;
    }

    override set width(value: number) {
        if (this.#width === value) {
            return;
        }
        this.#width = value;
        this.requiresRedraw = true;
    }

    override get height() {
        return this.#height;
    }

    override set height(value: number) {
        if (this.#height === value) {
            return;
        }
        this.#height = value;
        this.requiresRedraw = true;
    }

    get maxWidth() {
        return this.#width;
    }

    set maxWidth(value: number) {
        this.width = value;
    }

    get maxHeight() {
        return this.#height;
    }

    set maxHeight(value: number) {
        this.height = value;
    }

    getVisibleResolution(): { width: number; height: number } {
        return { width: this.#width, height: this.#height };
    }

    override get approximateMaxBounds(): PIXI.Rectangle {
        const freeParent = this.getFreelyPositionedParent();
        const transformation = freeParent.transformation;
        const { width, height } = this.getVisibleResolution();
        const { x, y } = getPositionFromTransformation(transformation, width, height);
        return { x, y, width, height } as PIXI.Rectangle;
    }

    override applyTransformation(offsetX = 0, offsetY = 0, offsetRotation = 0) {
        this._pixiContainer.alpha = this.opacity;
        if (this.isPositionControlledByParent) {
            return;
        }
        const { width, height } = this.getVisibleResolution();
        const { x, y } = getPositionFromTransformation(
            this.transformation.save() as TransformationType,
            width,
            height,
        );

        this._pixiContainer.x = x + offsetX;
        this._pixiContainer.y = y + offsetY;
        this._pixiContainer.rotation = this.transformation.rotation + offsetRotation;
    }

    get config(): ChartConfig | null {
        return this.#config;
    }

    set config(value: ChartConfig | null) {
        if (this.#config === value) {
            return;
        }
        this.#config = value;
        this.#resolvedSource = null;
        this.requiresRedraw = true;
    }

    applyColorPermutation(permutation: number[]): void {
        if (!this.#config) {
            return;
        }
        const remap = (node: unknown): unknown => {
            if (typeof node === 'string') {
                const match = node.match(/^themeRef_colors_(\d+)$/);
                if (!match) return node;
                const oldIndex = parseInt(match[1], 10);
                const newIndex = permutation[oldIndex];
                if (typeof newIndex !== 'number' || newIndex === oldIndex) return node;
                return `themeRef_colors_${newIndex}`;
            }
            if (Array.isArray(node)) {
                return node.map(remap);
            }
            if (node && typeof node === 'object') {
                const result: Record<string, unknown> = {};
                for (const key in node as Record<string, unknown>) {
                    result[key] = remap((node as Record<string, unknown>)[key]);
                }
                return result;
            }
            return node;
        };
        this.config = remap(this.#config) as ChartConfig;
    }

    @StringAttribute()
    accessor chartType: ChartSeriesType | '' = '';

    @StringAttribute()
    accessor chartData = '';

    @StringAttribute()
    accessor chartPrompt = '';

    #getPalette(): ChartPalette {
        const colors: string[] = [];
        if (this._templateOrNull?.colors) {
            for (const templateColor of this.template.colors) {
                const hexColor = '#' + templateColor.color.toString(16).padStart(6, '0');
                colors.push(hexColor);
            }
        }
        // `template.fonts` is an array of URLs. FontManager loads each via
        // `FontLoader.load()` which registers `@font-face { font-family: <hash-classname> }`.
        // ECharts draws text on canvas with `ctx.font`, which only accepts CSS-registered
        // family names — so we resolve each URL to its `familyName` here, then
        // `themeRef_fonts_<N>` strings in the chart config map cleanly to it.
        //
        // Use `addFont` (not `getFontBySourceIfLoaded`): `familyName` is the hash of the URL
        // and is available synchronously, even before the font has finished loading. This
        // also triggers loading as a side effect so subsequent renders pick up the real font.
        const fonts: string[] = [];
        if (this._templateOrNull?.fonts) {
            const fontManager = getGlobalFontManager();
            for (const fontUrl of this.template.fonts) {
                const font = fontManager.addFont(fontUrl);
                fonts.push(font.familyName);
            }
        }
        return {
            colors,
            fonts,
            background: 'transparent',
            text: '#8b949e',
            axis: '#30363d',
            grid: '#21262d',
        };
    }

    override _hasTemplateReferenceValues(): boolean {
        if (!this.#config) {
            return false;
        }
        return true;
    }

    override updateForRender() {
        if (!this.visible || !this.#resolvedConfig) {
            return;
        }

        this.#sprite.texture = this._reusableTextures.allocateTexture(() => {
            resetSprite(this.#sprite);
        });

        const sharedTexture = this.#sprite.texture;
        const canvas = (sharedTexture.baseTexture.resource as PIXI.BaseImageResource)
            .source as HTMLCanvasElement;

        const chartAspect = this.#width / this.#height;
        const baseTexW = sharedTexture.width;
        const baseTexH = sharedTexture.height;
        let frameW: number;
        let frameH: number;
        if (baseTexW / baseTexH > chartAspect) {
            frameH = baseTexH;
            frameW = frameH * chartAspect;
        } else {
            frameW = baseTexW;
            frameH = frameW / chartAspect;
        }
        const canvasScale = canvas.width / baseTexW;
        const destWidthPx = frameW * canvasScale;
        const destHeightPx = frameH * canvasScale;

        const ctx = canvas.getContext('2d');
        ctx?.clearRect(0, 0, canvas.width, canvas.height);
        ctx?.resetTransform();
        this.#renderer.renderToCanvas(
            canvas,
            this.#resolvedConfig,
            this.#progress,
            destWidthPx,
            destHeightPx,
        );

        const frame = new PIXI.Rectangle(0, 0, frameW, frameH);
        sharedTexture.frame = frame;
        sharedTexture.orig = frame;
        sharedTexture.trim = frame;

        this.#sprite.width = this.#width;
        this.#sprite.height = this.#height;

        sharedTexture.update();
    }

    #resolveThemeRef(value: unknown, palette: ChartPalette): unknown {
        if (typeof value !== 'string') return value;

        const colorMatch = value.match(/^themeRef_colors_(\d+)$/);
        if (colorMatch && palette?.colors) {
            const index = parseInt(colorMatch[1], 10);
            return palette.colors[index] || value;
        }

        const fontMatch = value.match(/^themeRef_fonts_(\d+)$/);
        if (fontMatch && palette?.fonts) {
            const index = parseInt(fontMatch[1], 10);
            return palette.fonts[index] || value;
        }

        const refMatch = value.match(/^themeRef_(\w+)$/);
        if (refMatch && palette) {
            const key = refMatch[1] as keyof ChartPalette;
            const resolved = palette[key];
            if (typeof resolved === 'string') return resolved;
        }

        return value;
    }

    #refreshResolvedConfig() {
        if (!this.#config) {
            this.#resolvedConfig = null;
            this.#resolvedSource = null;
            return;
        }
        const palette = this.#getPalette();
        const paletteKey = palette.colors.join('|') + '||' + palette.fonts.join('|');
        const cached = this.#resolvedSource;
        if (cached && cached.rawConfig === this.#config && cached.paletteKey === paletteKey) {
            return;
        }
        const deserialized = deserializeFunctions(this.#config) as ChartConfig;
        this.#resolvedConfig = {
            ...deserialized,
            echarts: applyLabelOverflowDefaults(
                this.#resolveAllRefs(deserialized.echarts, palette),
            ),
        };
        this.#resolvedSource = { rawConfig: this.#config, paletteKey };
    }

    #resolveAllRefs<T>(obj: T, palette: ChartPalette): T {
        if (obj === null || obj === undefined) return obj;

        if (typeof obj === 'string') {
            return this.#resolveThemeRef(obj, palette) as T;
        }

        if (Array.isArray(obj)) {
            return obj.map((item) => this.#resolveAllRefs(item, palette)) as T;
        }

        if (typeof obj === 'object') {
            const result: Record<string, unknown> = {};
            for (const key in obj) {
                result[key] = this.#resolveAllRefs((obj as Record<string, unknown>)[key], palette);
            }
            return result as T;
        }

        return obj;
    }

    override _build() {
        this.clearAnimation();

        this.#refreshResolvedConfig();

        super._build();

        if (this.hasDynamicStyle || this.hasMorphAnimation) {
            this._prepareDynamicStylingForUpdate();
            this.applyTransformation();
            this._applyAnimation();
            this._updateDynamicStyling();
        } else {
            this.applyTransformation();
            this._applyAnimation();
        }

        this._applyIntroAnimations();
        this._applyOutroAnimations();
        this.animationManager.setInitValues();
    }

    override _draw() {
        if (!this.#config) {
            return;
        }

        this.#sprite.width = this.#width;
        this.#sprite.height = this.#height;
    }

    override _seek(progress: number, seekMode: SeekMode) {
        super._seek(progress, seekMode);

        if (!this.#config) return;

        const animationDuration = 1500;
        const sceneDuration = this.container?.duration ?? animationDuration;
        const animationFraction = Math.min(1, animationDuration / sceneDuration);
        const rawChartProgress = Math.min(1, Math.max(0, progress) / animationFraction);
        const quantized = Math.round(rawChartProgress * 50) / 50;

        this.#progress = Math.max(0, Math.min(1, quantized));
    }

    _configCustomSave() {
        return this.#config ? serializeFunctions(this.#config) : null;
    }

    _configCustomLoad(configData: ChartConfig | null, _settings: LoadSettings) {
        this.#config = configData;
        this.#resolvedSource = null;
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat([
            'mask',
            'maxWidth',
            'maxHeight',
            'config',
            'chartType',
            'chartData',
            'chartPrompt',
            'createdFrom',
        ]);
    }
}
